import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePublisherAuth } from '../../context/PublisherAuthContext'
import {
  ArrowLeft,
  Tag,
  Zap,
  AlertCircle,
  Check,
  Server,
} from 'lucide-react'

function HelpTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (show && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPos({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      })
    }
  }, [show])

  return (
    <span className="relative inline-block ml-1">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setShow(!show)}
        onBlur={() => setShow(false)}
        className="w-4 h-4 rounded-full bg-gray-200 text-gray-500 hover:bg-gray-300 text-xs inline-flex items-center justify-center"
      >
        ?
      </button>
      {show && (
        <div
          className="fixed w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg"
          style={{ top: pos.top, left: pos.left, transform: 'translate(-50%, -100%)', zIndex: 9999 }}
        >
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 rotate-45" />
        </div>
      )}
    </span>
  )
}

const EXAMPLE_CAPABILITIES = [
  'data.read', 'data.write', 'data.query',
  'file.read', 'file.write', 'file.list',
  'api.call', 'api.webhook',
  'search.web', 'search.index',
  'ai.generate', 'ai.analyze', 'ai.classify',
  'auth.login', 'auth.verify',
  'notify.email', 'notify.push',
]

const POPULAR_TAGS = [
  'utility', 'api', 'data', 'productivity', 'developer',
  'ai', 'automation', 'security', 'monitoring', 'analytics',
  'database', 'cloud', 'communication', 'search', 'testing',
]

const CAPABILITY_PATTERN = /^[a-zA-Z][a-zA-Z0-9]*\.[a-zA-Z][a-zA-Z0-9]*$/

export default function PublisherNewPackagePage() {
  const { token } = usePublisherAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState(1)
  const [showErrors, setShowErrors] = useState(false)
  const [capabilityError, setCapabilityError] = useState('')

  // Slug uniqueness check state
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const slugTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    summary: '',
    description: '',
    capabilities: [] as string[],
    tags: [] as string[],
    server_url: '',
    repository_url: '',
    documentation_url: '',
  })

  const [newCapability, setNewCapability] = useState('')
  const [newTag, setNewTag] = useState('')

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
  }

  // Debounced slug uniqueness check
  useEffect(() => {
    if (!formData.slug) {
      setSlugStatus('idle')
      return
    }

    setSlugStatus('checking')

    if (slugTimerRef.current) {
      clearTimeout(slugTimerRef.current)
    }

    slugTimerRef.current = setTimeout(async () => {
      try {
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
        const response = await fetch(`${API_BASE_URL}/v1/resolve?slug=${encodeURIComponent(formData.slug)}`)
        if (response.ok) {
          // Slug already exists
          setSlugStatus('taken')
        } else if (response.status === 404) {
          setSlugStatus('available')
        } else {
          setSlugStatus('idle')
        }
      } catch {
        setSlugStatus('idle')
      }
    }, 500)

    return () => {
      if (slugTimerRef.current) {
        clearTimeout(slugTimerRef.current)
      }
    }
  }, [formData.slug])

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: slugManuallyEdited ? prev.slug : generateSlug(name),
    }))
  }

  const addCapability = (cap?: string) => {
    const value = cap || newCapability
    if (!value) return

    if (!CAPABILITY_PATTERN.test(value)) {
      setCapabilityError('Capabilities must be in namespace.action format (e.g., weather.forecast)')
      return
    }

    if (!formData.capabilities.includes(value)) {
      setFormData((prev) => ({
        ...prev,
        capabilities: [...prev.capabilities, value],
      }))
    }
    setNewCapability('')
    setCapabilityError('')
  }

  const removeCapability = (cap: string) => {
    setFormData((prev) => ({
      ...prev,
      capabilities: prev.capabilities.filter((c) => c !== cap),
    }))
  }

  const addTag = (tag?: string) => {
    const value = (tag || newTag).toLowerCase()
    if (value && !formData.tags.includes(value)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, value],
      }))
    }
    setNewTag('')
  }

  const removeTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }))
  }

  const handleSubmit = async () => {
    setError('')
    setLoading(true)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
      const response = await fetch(`${API_BASE_URL}/v1/packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.message || 'Failed to create package')
      }

      const data = await response.json()
      navigate(`/publisher/packages/${data.package_id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create package')
    } finally {
      setLoading(false)
    }
  }

  const isStep1Valid = formData.name && formData.slug && formData.summary && slugStatus !== 'taken'
  const isStep2Valid = formData.capabilities.length > 0
  const isStep3Valid = !!formData.server_url

  const handleContinue = () => {
    if (step === 1 && !isStep1Valid) {
      setShowErrors(true)
      return
    }
    if (step === 2 && !isStep2Valid) {
      setShowErrors(true)
      return
    }
    setShowErrors(false)
    setStep(step + 1)
  }

  const handleCreateClick = () => {
    if (!isStep3Valid) {
      setShowErrors(true)
      return
    }
    setShowErrors(false)
    handleSubmit()
  }

  const unusedCapSuggestions = EXAMPLE_CAPABILITIES.filter(
    (c) => !formData.capabilities.includes(c)
  )

  const unusedTagSuggestions = POPULAR_TAGS.filter(
    (t) => !formData.tags.includes(t)
  )

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/publisher/packages')}
          className="flex items-center text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to packages
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create New Package</h1>
        <p className="text-gray-500 mt-1">
          Set up your MCP skill package for publication
        </p>
      </div>

      {/* Progress steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= s
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {step > s ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`w-24 h-1 mx-2 ${
                    step > s ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-sm text-gray-500">
          <span>Basic Info</span>
          <span>Capabilities</span>
          <span>Details</span>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Form */}
      <div className="card p-6">
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Package Name *
                <HelpTooltip text="The display name for your skill. Choose something descriptive and memorable." />
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                className={`input w-full ${showErrors && !formData.name ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                placeholder="My Awesome Skill"
              />
              {showErrors && !formData.name && (
                <p className="text-xs text-red-600 mt-1">Required</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slug *
                <HelpTooltip text="A URL-friendly identifier used in xarlie.io/skills/your-slug. Must be lowercase with hyphens only." />
              </label>
              <div className="flex items-center">
                <span className="text-gray-500 mr-2">xarlie.io/skills/</span>
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => {
                      setSlugManuallyEdited(true)
                      setFormData((prev) => ({ ...prev, slug: e.target.value }))
                    }}
                    className={`input w-full ${showErrors && !formData.slug ? 'border-red-500 ring-1 ring-red-500' : ''} ${slugStatus === 'taken' ? 'border-red-500 ring-1 ring-red-500' : ''} ${slugStatus === 'available' ? 'border-green-500 ring-1 ring-green-500' : ''}`}
                    placeholder="my-awesome-skill"
                  />
                  {slugStatus === 'checking' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">Checking...</span>
                  )}
                  {slugStatus === 'available' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">
                      <Check className="w-4 h-4" />
                    </span>
                  )}
                  {slugStatus === 'taken' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-600">
                      <AlertCircle className="w-4 h-4" />
                    </span>
                  )}
                </div>
              </div>
              {slugStatus === 'taken' && (
                <p className="text-xs text-red-600 mt-1">This slug is already taken. Please choose another.</p>
              )}
              {slugStatus === 'available' && (
                <p className="text-xs text-green-600 mt-1">This slug is available!</p>
              )}
              {showErrors && !formData.slug && (
                <p className="text-xs text-red-600 mt-1">Required</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                URL-friendly identifier for your package
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Summary *
                <HelpTooltip text="A concise one-line description shown in search results. Max 160 characters." />
              </label>
              <input
                type="text"
                value={formData.summary}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, summary: e.target.value }))
                }
                className={`input w-full ${showErrors && !formData.summary ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                placeholder="A brief description of what your skill does"
                maxLength={160}
              />
              {showErrors && !formData.summary ? (
                <p className="text-xs text-red-600 mt-1">Required</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {formData.summary.length}/160 characters
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
                <HelpTooltip text="A detailed description of your skill's features. Supports plain text. Shown on the skill detail page." />
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                className="input w-full h-32"
                placeholder="Detailed description of your skill's features and use cases..."
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Zap className="w-4 h-4 inline mr-1" />
                Capabilities *
                <HelpTooltip text="Define what your skill can do using namespace.action format (e.g., weather.forecast, db.query). These help users discover your skill." />
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Define the tools/capabilities your package provides (e.g., weather.current, math.calculate)
              </p>
              <div className="flex space-x-2 mb-1">
                <input
                  type="text"
                  value={newCapability}
                  onChange={(e) => { setNewCapability(e.target.value); setCapabilityError('') }}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCapability())}
                  className={`input flex-1 ${capabilityError ? 'border-red-500 ring-1 ring-red-500' : ''} ${showErrors && formData.capabilities.length === 0 ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  placeholder="namespace.action"
                />
                <button
                  type="button"
                  onClick={() => addCapability()}
                  className="btn btn-secondary"
                >
                  Add
                </button>
              </div>
              {capabilityError && (
                <p className="text-xs text-red-600 mb-2">{capabilityError}</p>
              )}
              {showErrors && formData.capabilities.length === 0 && !capabilityError && (
                <p className="text-xs text-red-600 mb-2">Required - add at least one capability</p>
              )}
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700"
                  >
                    {cap}
                    <button
                      type="button"
                      onClick={() => removeCapability(cap)}
                      className="ml-2 hover:text-primary-900"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              {unusedCapSuggestions.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Suggestions:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {unusedCapSuggestions.map((cap) => (
                      <button
                        key={cap}
                        type="button"
                        onClick={() => addCapability(cap)}
                        className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                      >
                        {cap}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="w-4 h-4 inline mr-1" />
                Tags
                <HelpTooltip text="Keywords that help users find your skill in search. Use lowercase, common terms." />
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Add tags to help users discover your package
              </p>
              <div className="flex space-x-2 mb-3">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="input flex-1"
                  placeholder="utility, api, data..."
                />
                <button
                  type="button"
                  onClick={() => addTag()}
                  className="btn btn-secondary"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 hover:text-gray-900"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              {unusedTagSuggestions.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Popular tags:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {unusedTagSuggestions.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => addTag(tag)}
                        className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Server className="w-4 h-4 inline mr-1" />
                Server URL *
                <HelpTooltip text="The URL where your MCP server is hosted. Required for users to connect to your skill. Must be a valid HTTPS URL." />
              </label>
              <input
                type="url"
                value={formData.server_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, server_url: e.target.value }))
                }
                className={`input w-full ${showErrors && !formData.server_url ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                placeholder="https://mcp.example.com"
              />
              {showErrors && !formData.server_url && (
                <p className="text-xs text-red-600 mt-1">Required</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Repository URL
                <HelpTooltip text="Link to the source code repository (e.g., GitHub). Helps build trust with users." />
              </label>
              <input
                type="url"
                value={formData.repository_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, repository_url: e.target.value }))
                }
                className="input w-full"
                placeholder="https://github.com/username/repo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documentation URL
                <HelpTooltip text="Link to your skill's documentation. Helps users get started." />
              </label>
              <input
                type="url"
                value={formData.documentation_url}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, documentation_url: e.target.value }))
                }
                className="input w-full"
                placeholder="https://docs.example.com"
              />
            </div>

            {/* Summary */}
            <div className="border-t pt-6 mt-6">
              <h3 className="font-medium text-gray-900 mb-4">Review your package</h3>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Name</dt>
                  <dd className="font-medium">{formData.name}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Slug</dt>
                  <dd className="font-mono text-sm">{formData.slug}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Server URL</dt>
                  <dd className="font-mono text-sm truncate max-w-[300px]">{formData.server_url || <span className="text-red-500">Not set</span>}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Capabilities</dt>
                  <dd>{formData.capabilities.length} defined</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Tags</dt>
                  <dd>{formData.tags.length} added</dd>
                </div>
              </dl>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => { setStep(step - 1); setShowErrors(false) }}
              className="btn btn-secondary"
            >
              Previous
            </button>
          ) : (
            <div />
          )}
          {step < 3 ? (
            <button
              type="button"
              onClick={handleContinue}
              className="btn btn-primary"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCreateClick}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Creating...' : 'Create Package'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
